/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import { useGetStationOnboardingPhotosQuery, useGetTokaStationPhotosQuery } from '../../redux/api';
import { HOST, PORT } from '../../environment';
import { WebCamera } from '../webCamera/WebCamera';
import './stationMediaCarousel.css';

function resolvePhotoUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const base = `${HOST}:${PORT}`.replace(/\/+$/, '');
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
}

function PhotoLightbox({ slides, index, liveHostRef, onChange, onClose }) {
  const dragRef = useRef({ active: false, startX: 0 });
  const movedRef = useRef(false);
  const [dx, setDx] = useState(0);
  const [sliding, setSliding] = useState(true);
  const lastIndex = slides.length - 1;

  const step = useCallback(
    delta => {
      onChange(Math.min(lastIndex, Math.max(0, index + delta)));
    },
    [index, lastIndex, onChange]
  );

  useEffect(() => {
    const onKey = event => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, step]);

  const finishDrag = clientX => {
    if (!dragRef.current.active) return;
    const delta = clientX - dragRef.current.startX;
    dragRef.current.active = false;
    setDx(0);
    setSliding(true);
    if (delta <= -48) step(1);
    else if (delta >= 48) step(-1);
  };

  return createPortal(
    <div
      className="station-photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Station camera and photos"
      onClick={event => {
        if (
          event.target.closest('button') ||
          event.target.closest('img') ||
          event.target.closest('video') ||
          event.target.closest('.station-photo-lightbox__live')
        ) {
          return;
        }
        if (movedRef.current) {
          movedRef.current = false;
          return;
        }
        onClose();
      }}
      onPointerDown={event => {
        if (event.target.closest('button')) return;
        dragRef.current = { active: true, startX: event.clientX };
        movedRef.current = false;
        setSliding(false);
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }}
      onPointerMove={event => {
        if (!dragRef.current.active) return;
        let delta = event.clientX - dragRef.current.startX;
        if (Math.abs(delta) > 8) movedRef.current = true;
        if ((index === 0 && delta > 0) || (index === lastIndex && delta < 0)) delta *= 0.35;
        setDx(delta);
      }}
      onPointerUp={event => finishDrag(event.clientX)}
      onPointerCancel={event => finishDrag(event.clientX)}
    >
      <div className="station-photo-lightbox__stage">
        {index > 0 ? (
          <button
            type="button"
            className="station-photo-lightbox__nav station-photo-lightbox__nav--prev"
            aria-label="Previous photo"
            onClick={() => step(-1)}
          >
            ‹
          </button>
        ) : null}
        {index < lastIndex ? (
          <button
            type="button"
            className="station-photo-lightbox__nav station-photo-lightbox__nav--next"
            aria-label="Next photo"
            onClick={() => step(1)}
          >
            ›
          </button>
        ) : null}
        <div className="station-photo-lightbox__viewport">
          <div
            className={`station-photo-lightbox__track${sliding ? ' station-photo-lightbox__track--sliding' : ''}`}
            style={{ transform: `translate3d(calc(${-index * 100}vw + ${dx}px), 0, 0)` }}
          >
            {slides.map((slide, slideIndex) =>
              slide.type === 'live' ? (
                <div className="station-photo-lightbox__slide" key="live">
                  <div className="station-photo-lightbox__live" ref={liveHostRef} />
                </div>
              ) : (
                <div className="station-photo-lightbox__slide" key={`${slide.url}-${slideIndex}`}>
                  <img src={slide.url} alt="" draggable="false" />
                </div>
              )
            )}
          </div>
        </div>
      </div>
      <button type="button" className="station-photo-lightbox__close" aria-label="Close" onClick={onClose}>
        ×
      </button>
    </div>,
    document.body
  );
}

/**
 * One horizontal carousel for live HLS + station photos.
 * When the stream is loading or offline, photos are shown (live slide stays hidden until playing).
 */
export function StationMediaCarousel({ stationNumber, protocolType, webcamUrl }) {
  const isTokaStation = protocolType === 'ROAMING_TOKA';
  const onboarding = useGetStationOnboardingPhotosQuery({ stationNumber }, { skip: !stationNumber });
  const toka = useGetTokaStationPhotosQuery({ stationNumber }, { skip: !stationNumber || !isTokaStation });
  const [hlsStatus, setHlsStatus] = useState(webcamUrl ? 'loading' : 'none');
  const [openIndex, setOpenIndex] = useState(null);
  const [liveSlotAttached, setLiveSlotAttached] = useState(false);
  const closeLightbox = useCallback(() => setOpenIndex(null), []);
  const liveSlotRef = useRef(null);
  const stripHostRef = useRef(null);
  const lightboxHostRef = useRef(null);
  if (liveSlotRef.current == null && typeof document !== 'undefined') {
    const slot = document.createElement('div');
    slot.className = 'station-media-live-slot';
    liveSlotRef.current = slot;
  }

  useEffect(() => {
    setHlsStatus(webcamUrl ? 'loading' : 'none');
  }, [webcamUrl]);

  const photos = useMemo(
    () => [...(onboarding.data?.photos || []), ...(toka.data?.photos || [])].map(resolvePhotoUrl).filter(Boolean),
    [onboarding.data?.photos, toka.data?.photos]
  );

  const showLiveSlide = Boolean(webcamUrl) && hlsStatus === 'playing';
  const keepHlsMounted = Boolean(webcamUrl) && hlsStatus !== 'error' && hlsStatus !== 'none';
  const hasPhotos = photos.length > 0;
  const slides = useMemo(() => {
    const items = photos.map(url => ({ type: 'photo', url }));
    return showLiveSlide ? [{ type: 'live' }, ...items] : items;
  }, [photos, showLiveSlide]);
  const liveShownRef = useRef(showLiveSlide);

  useEffect(() => {
    if (showLiveSlide && !liveShownRef.current && openIndex != null) {
      setOpenIndex(current => (current == null ? current : current + 1));
    }
    liveShownRef.current = showLiveSlide;
  }, [showLiveSlide, openIndex]);

  useEffect(() => {
    const slot = liveSlotRef.current;
    const host = openIndex != null ? lightboxHostRef.current : stripHostRef.current;
    if (slot && host && slot.parentElement !== host) host.appendChild(slot);
    if (slot?.isConnected) setLiveSlotAttached(true);
  }, [openIndex, showLiveSlide, keepHlsMounted]);

  if (!webcamUrl && !hasPhotos) {
    return null;
  }

  return (
    <Box className="station-media-carousel-wrap">
      <div className="station-media-carousel" aria-label="Station camera and photos">
        {keepHlsMounted ? (
          <div
            ref={stripHostRef}
            className={
              showLiveSlide
                ? 'station-media-carousel__slide station-media-carousel__slide--live'
                : 'station-media-carousel__hls-probe'
            }
            aria-hidden={!showLiveSlide}
            role={showLiveSlide ? 'button' : undefined}
            tabIndex={showLiveSlide ? 0 : undefined}
            onClick={() => {
              if (showLiveSlide) setOpenIndex(0);
            }}
            onKeyDown={event => {
              if (showLiveSlide && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                setOpenIndex(0);
              }
            }}
          />
        ) : null}
        {keepHlsMounted && liveSlotAttached && liveSlotRef.current
          ? createPortal(<WebCamera webcamUrl={webcamUrl} onStatusChange={setHlsStatus} />, liveSlotRef.current)
          : null}

        {!showLiveSlide && !hasPhotos && hlsStatus === 'loading' ? (
          <div className="station-media-carousel__slide station-media-carousel__slide--placeholder">
            <span className="station-media-carousel__loading">…</span>
          </div>
        ) : null}

        {photos.map((url, index) => (
          <button
            key={`${url}-${index}`}
            type="button"
            className="station-media-carousel__slide station-media-carousel__slide--photo"
            onClick={() => setOpenIndex(showLiveSlide ? index + 1 : index)}
          >
            <img src={url} alt="" loading="lazy" />
          </button>
        ))}
      </div>
      {openIndex != null && slides[openIndex] ? (
        <PhotoLightbox
          slides={slides}
          index={openIndex}
          liveHostRef={lightboxHostRef}
          onChange={setOpenIndex}
          onClose={closeLightbox}
        />
      ) : null}
    </Box>
  );
}
